import { useCallback } from "react";

import { listSoilSightScenarios } from "services";

import { useDeleteSoilSightScenario, useNavigate, useUserContext } from "utils";

import { useFieldImportPersist } from "pages/app/scenario-analysis/hooks/useFieldImportPersist";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { useGetSoilSightOnboardingSetting, useUpdateSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { resolveOnboardingEntryRoute } from "pages/app/scenario-onboarding/utils/flowRoutes";
import { hasPassedLandingForFarm, mergeInProgressDraft } from "pages/app/scenario-onboarding/utils/onboardingState";
import { applyPracticeDefaults } from "pages/app/scenario-onboarding/utils/practiceDefaults";

export type SubmitFieldImportViaOnboardingArgs = {
  klimFarmId: string;
  fieldId: string;
  cropYears: CropYear[];
};

/**
 * Field-management → SoilSight import for a user who has NOT completed onboarding: writes the
 * in-progress key before any project mutation, OVERRIDES the baseline with the imported rotation,
 * deletes change scenarios that no longer match it, then routes into the flow.
 */
export const useSoilSightFieldImportViaOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { onboardingState } = useGetSoilSightOnboardingSetting();

  const { persistFieldImport, refreshAfterImport, showImportError, isPersisting } = useFieldImportPersist();
  const deleteScenario = useDeleteSoilSightScenario();
  const updateOnboardingSetting = useUpdateSoilSightOnboardingSetting();

  const isSubmitting = isPersisting || deleteScenario.isPending;

  const submitFieldImportViaOnboarding = useCallback(
    async ({ klimFarmId, fieldId, cropYears }: SubmitFieldImportViaOnboardingArgs): Promise<boolean> => {
      try {
        // A first-time user (no prior flow action) gets the landing
        // page after the import; a user who already passed it resumes at step 1 directly.
        const hasPassedLanding = hasPassedLandingForFarm(onboardingState, klimFarmId);

        // In-progress key BEFORE project creation (grandfathering disambiguation). A fresh
        // import brings a fresh banner, so the previous dismissal is explicitly reset.
        await updateOnboardingSetting.mutateAsync(
          mergeInProgressDraft(onboardingState, {
            farmId: klimFarmId,
            importedFieldId: fieldId,
            importBannerDismissed: false,
          })
        );

        // Imported rows with missing practices get the onboarding defaults before persisting.
        const cropYearsWithDefaults = cropYears.map(applyPracticeDefaults);

        const existingProjectId = user?.farm?.soilSightProjectId ?? null;
        const persistResult = await persistFieldImport({
          klimFarmId,
          fieldId,
          cropYears: cropYearsWithDefaults,
          getTargetProjectId: () => existingProjectId,
        });

        // The imported baseline invalidates any previously created change scenarios. The
        // persist already fetched the list; the deletes are independent — run them in parallel.
        if (existingProjectId) {
          const scenarios = persistResult.scenarios ?? (await listSoilSightScenarios(persistResult.soilSightProjectId));
          const changeScenarios = scenarios.filter((scenario) => !scenario.isReference);
          await Promise.all(changeScenarios.map((scenario) => deleteScenario.mutateAsync(scenario.id)));
        }

        await refreshAfterImport({ soilSightProjectId: persistResult.soilSightProjectId, invalidateScenarios: true });

        // The rotation itself is restored from the server; only the dropped-crops info
        // travels via router state, since the import response is the sole place it exists.
        navigate(resolveOnboardingEntryRoute(hasPassedLanding), {
          replace: true,
          state: { droppedMainCrops: persistResult.droppedMainCrops, entryPoint: "field_cell" },
        });
        return true;
      } catch (submitError) {
        showImportError(submitError);
        return false;
      }
    },
    [
      deleteScenario,
      navigate,
      onboardingState,
      persistFieldImport,
      refreshAfterImport,
      showImportError,
      updateOnboardingSetting,
      user?.farm?.soilSightProjectId,
    ]
  );

  return { submitFieldImportViaOnboarding, isSubmitting };
};
